import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import {
  DodoPaymentTransactionRow,
  fetchDodoAdminTransactions,
  formatTransactionAmount,
} from '../../services/adminDodoTransactionService';

function formatWhen(d: Date | null): string {
  if (!d) return '—';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'medium',
  }).format(d);
}

function outcomeBadgeClass(outcome: string): string {
  const o = outcome.toLowerCase();
  if (o === 'success') return 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100';
  if (o === 'failed') return 'bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-100';
  if (o === 'cancelled') return 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100';
  if (o === 'processing') return 'bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-100';
  return 'bg-slate-100 text-slate-800 dark:bg-neutral-800 dark:text-slate-200';
}

const AdminDodoTransactionsPage: React.FC = () => {
  const { isDark } = useAdminTheme();
  const { showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<DodoPaymentTransactionRow[]>([]);
  const [filter, setFilter] = useState<'all' | 'success' | 'failed'>('all');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchDodoAdminTransactions();
      setRows(data);
    } catch (e) {
      console.error('AdminDodoTransactionsPage', e);
      showError('Could not load Dodo transactions. Deploy latest functions and Firestore rules if this is new.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (filter === 'all') return rows;
    if (filter === 'success') {
      return rows.filter((r) => String(r.outcomeStatus).toLowerCase() === 'success');
    }
    return rows.filter((r) => {
      const o = String(r.outcomeStatus).toLowerCase();
      return o === 'failed' || o === 'cancelled';
    });
  }, [rows, filter]);

  const tableWrap = isDark ? 'border-neutral-700 bg-neutral-900/50' : 'border-slate-200 bg-white';

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-cyan-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className={`max-w-3xl text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
          Combines <code className="text-xs">dodoPaymentTransactions</code>,{' '}
          <code className="text-xs">dodoWebhookDeliveries</code>,{' '}
          <code className="text-xs">dodoWebhookProcessedPayments</code>, and <code className="text-xs">invoices</code>.
          User email is loaded from <code className="text-xs">users</code> by Firebase UID; payment method / discount /
          amount come from Dodo when the latest functions have written them (re-deploy after upgrade).
        </p>
        <div className="flex items-center gap-2">
          <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Show</span>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as 'all' | 'success' | 'failed')}
            className={`rounded-lg border px-2 py-1 text-sm ${
              isDark
                ? 'border-neutral-600 bg-neutral-900 text-white'
                : 'border-slate-300 bg-white text-slate-900'
            }`}
          >
            <option value="all">All</option>
            <option value="success">Succeeded</option>
            <option value="failed">Failed / cancelled</option>
          </select>
          <button
            type="button"
            onClick={() => load()}
            className={`rounded-lg px-3 py-1 text-sm font-medium ${
              isDark ? 'bg-neutral-800 text-white hover:bg-neutral-700' : 'bg-slate-100 text-slate-900 hover:bg-slate-200'
            }`}
          >
            Refresh
          </button>
        </div>
      </div>

      <div className={`overflow-x-auto rounded-xl border shadow-sm ${tableWrap}`}>
        <table className="min-w-[1320px] w-full text-left text-sm">
          <thead>
            <tr className={`border-b ${isDark ? 'border-neutral-700 bg-neutral-900' : 'border-slate-200 bg-slate-50'}`}>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Timestamp</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Record ID</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Status</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">User email</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Payment ID</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Subscription ID</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Payment method</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Discount code</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Amount</th>
              <th className="whitespace-nowrap px-3 py-2 font-semibold">Source / event</th>
              <th className="px-3 py-2 font-semibold">Details</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={11} className={`px-3 py-8 text-center ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                  No rows matched. Trigger a test checkout or Dodo webhook, confirm your Firebase project matches
                  this app, and deploy Firestore rules so admins can read the Dodo collections listed above.
                </td>
              </tr>
            ) : (
              filtered.map((r) => (
                <tr
                  key={r.rowKey}
                  className={`border-b last:border-0 ${
                    isDark ? 'border-neutral-800 hover:bg-neutral-800/60' : 'border-slate-100 hover:bg-slate-50'
                  }`}
                >
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{formatWhen(r.createdAt)}</td>
                  <td className="max-w-[120px] truncate px-3 py-2 font-mono text-[11px]" title={r.id}>
                    {r.id}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${outcomeBadgeClass(r.outcomeStatus)}`}>
                      {r.outcomeStatus}
                    </span>
                    {r.dodoPaymentStatus && (
                      <span className={`ml-1 text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                        ({r.dodoPaymentStatus})
                      </span>
                    )}
                  </td>
                  <td className="max-w-[200px] px-3 py-2 text-xs">
                    <div className="truncate font-medium" title={r.userEmail || ''}>
                      {r.userEmail || '—'}
                    </div>
                    {r.firebaseUid && (
                      <div
                        className={`truncate font-mono text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                        title={r.firebaseUid}
                      >
                        uid {r.firebaseUid}
                      </div>
                    )}
                  </td>
                  <td className="max-w-[180px] truncate px-3 py-2 font-mono text-xs" title={r.dodoPaymentId || ''}>
                    {r.dodoPaymentId || '—'}
                  </td>
                  <td className="max-w-[180px] truncate px-3 py-2 font-mono text-xs" title={r.dodoSubscriptionId || ''}>
                    {r.dodoSubscriptionId || '—'}
                  </td>
                  <td className="max-w-[140px] truncate px-3 py-2 text-xs" title={r.paymentMethod || ''}>
                    {r.paymentMethod || '—'}
                  </td>
                  <td className="max-w-[120px] truncate px-3 py-2 font-mono text-xs" title={r.discountCode || ''}>
                    {r.discountCode || '—'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs font-medium">{formatTransactionAmount(r)}</td>
                  <td className="px-3 py-2 text-xs">
                    <div>{r.source}</div>
                    {r.eventType && <div className="text-slate-500">{r.eventType}</div>}
                    {r.webhookDeliveryId && (
                      <div className={`truncate font-mono ${isDark ? 'text-slate-500' : 'text-slate-500'}`} title={r.webhookDeliveryId}>
                        wh: {r.webhookDeliveryId}
                      </div>
                    )}
                    {r.httpStatus != null && (
                      <div className={isDark ? 'text-slate-500' : 'text-slate-500'}>HTTP {r.httpStatus}</div>
                    )}
                  </td>
                  <td className="max-w-md px-3 py-2 text-xs">
                    {[r.plan, r.billingPeriod].filter(Boolean).join(' · ') || null}
                    {r.invoiceId && (
                      <div className={isDark ? 'text-slate-400' : 'text-slate-600'}>Invoice: {r.invoiceId}</div>
                    )}
                    {r.checkoutSessionId && (
                      <div className="truncate font-mono text-slate-500" title={r.checkoutSessionId}>
                        session: {r.checkoutSessionId}
                      </div>
                    )}
                    {r.checkoutSource && (
                      <div className="text-slate-500">{r.checkoutSource}</div>
                    )}
                    {r.failureCode && (
                      <div className="text-red-600 dark:text-red-400">Code: {r.failureCode}</div>
                    )}
                    {r.failureMessage && (
                      <div className="whitespace-pre-wrap break-words text-red-700 dark:text-red-300">{r.failureMessage}</div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p className={`mt-3 text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
        Amount uses <code className="text-[10px]">amountUsd</code> when set; otherwise invoice / minor-unit rules
        (USD ÷ 100). Deploy updated <code className="text-[10px]">dodoPayments</code> functions so new webhooks and{' '}
        <code className="text-[10px]">dodoWebhookProcessedPayments</code> rows include method and discount metadata.
      </p>
    </div>
  );
};

export default AdminDodoTransactionsPage;
